import { useRef } from 'react';
import { toast } from 'sonner';
import { useTranslation } from '../../../i18n';
import {
  useUploadProfilePictureMutation,
  useDeleteProfilePictureMutation,
} from '../../userProfile.queries';

/**
 * Profile-picture click / upload / delete behaviour for the User Profile page
 * (STD-014 split out of UserProfilePage.tsx).
 *
 * Owns the hidden file-input ref and wires the upload/delete mutations to the
 * toast feedback exactly as the page did before — this is a WRITE path, so the
 * mutation calls and their success/error branching are preserved verbatim.
 */
export interface UseProfilePicture {
  fileInputRef: React.RefObject<HTMLInputElement>;
  isPending: boolean;
  handlePictureClick: () => void;
  handlePictureChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleDeletePicture: () => void;
}

export function useProfilePicture(
  userId: string | undefined,
  canEdit: boolean | undefined
): UseProfilePicture {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadPictureMutation = useUploadProfilePictureMutation(userId ?? '');
  const deletePictureMutation = useDeleteProfilePictureMutation(userId ?? '');

  const handlePictureClick = () => {
    if (canEdit) {
      fileInputRef.current?.click();
    }
  };

  const handlePictureChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !userId) return;

    uploadPictureMutation.mutate(file, {
      onSuccess: (result) => {
        if (result.success) {
          toast.success(t('profile.pictureUploaded'));
        } else {
          toast.error(result.error || t('profile.pictureUploadError'));
        }
      },
      onError: () => toast.error(t('profile.pictureUploadError')),
      onSettled: () => {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      },
    });
  };

  const handleDeletePicture = () => {
    if (!userId) return;
    deletePictureMutation.mutate(undefined, {
      onSuccess: (result) => {
        if (result.success) {
          toast.success(t('profile.pictureDeleted'));
        } else {
          toast.error(result.error || t('profile.pictureDeleteError'));
        }
      },
      onError: () => toast.error(t('profile.pictureDeleteError')),
    });
  };

  return {
    fileInputRef,
    isPending: uploadPictureMutation.isPending || deletePictureMutation.isPending,
    handlePictureClick,
    handlePictureChange,
    handleDeletePicture,
  };
}
